import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { isNullOrUndefined, MessageService } from '@sla-ng/core';
import { IMessageValue } from '@sla-ng/core/lib/message/message-value.model';
import { Moment } from 'moment';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { IAddPurchasePricePlanning } from '../../../models/add-planned-purchase-price.model';
import { IPlannedPurchasePrice } from '../../../models/planned-purchase-price.model';
import { IPlanningGenerationSelection } from '../../../models/planning-generation-selection.model';
import { IUpdatePurchasePricePlanning } from '../../../models/update-planned-purchase-price.model';
import { PlannedPurchasePriceResource } from '../../../resources/planned-purchase-price.resource';
import { IPlannedPurchasePriceState } from './planned-purchase-price-state.model';

let _state: IPlannedPurchasePriceState = {
    isLoading: false,
    data: [],
};

@Injectable({
    providedIn: 'root',
})
export class PlannedPurchasePriceFacade {
    protected store$: BehaviorSubject<IPlannedPurchasePriceState> = new BehaviorSubject<IPlannedPurchasePriceState>(_state);

    constructor(private myPlannedPurchasePriceResource: PlannedPurchasePriceResource,
                private myMessageService: MessageService,
                private myTranslateService: TranslateService) {
    }

    public getStateSnapshot(): IPlannedPurchasePriceState {
        return this.store$.value;
    }

    public subState(): Observable<IPlannedPurchasePriceState> {
        return this.store$.asObservable();
    }

    public clearState(): void {
        this.updateState({
            isLoading: false,
            data: [],
        });
    }

    public async fetchData(): Promise<void> {
        this.setIsloading(true);
        await this.fetchPlannedPrices();
        this.setIsloading(false);
    }

    public async fetchPlannedPrices(articleId?: number, from?: Moment, to?: Moment): Promise<void> {
        this.setIsloading(true);
        const data: IPlannedPurchasePrice[] = await firstValueFrom(this.myPlannedPurchasePriceResource.getPlannedPurchasePrices(articleId, from, to)).catch(() => []);
        this.updateState({ ..._state, data });
        this.setIsloading(false);
    }


    public async addPlannedPurchasePrice(data: IAddPurchasePricePlanning): Promise<void> {
        this.setIsloading(true);
        const createdPrice: IPlannedPurchasePrice = await firstValueFrom(this.myPlannedPurchasePriceResource.addPlannedPurchasePrice(data)).catch(() => null);
        if (createdPrice !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.planned-purchase-price.actions.added.label');
        }
        this.setIsloading(false);
    }

    public async deletePlannedPurchasePrice(price: IPlannedPurchasePrice): Promise<void> {
        this.setIsloading(true);
        try {
            await firstValueFrom(this.myPlannedPurchasePriceResource.deletePlannedPurchasePrice(price.id));
            this.myMessageService.successTrans('app.actions.feedback.deleted', 'app.page.planned-purchase-price.actions.deleted.label');
        } catch (e) {
            console.error(e);
        }
        this.setIsloading(false);
    }

    public async updatePlannedPurchasePrice(id: number, data: IUpdatePurchasePricePlanning): Promise<void> {
        this.setIsloading(true);
        const updatedPrice: IPlannedPurchasePrice = await firstValueFrom(this.myPlannedPurchasePriceResource.updatePlannedPurchasePrice(id, data)).catch(() => null);
        if (updatedPrice !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.planned-purchase-price.actions.edited.label');
        }
        this.setIsloading(false);
    }

    public async generatePlannedPrices(generationSelection: IPlanningGenerationSelection): Promise<void> {
        this.setIsloading(true);
        const generationSummary = await firstValueFrom(this.myPlannedPurchasePriceResource.generatePrices(generationSelection)).catch(() => null);
        if (!isNullOrUndefined(generationSummary)) {
            this.myTranslateService.instant('app.page.planning.actions.generated.label', generationSummary);
            const message: IMessageValue = { value: 'app.page.planning.actions.generated.label', translationParams: generationSummary };
            this.myMessageService.successTrans('app.actions.feedback.saved', message, 4);
        }
        this.setIsloading(false);
    }

    private updateState(state: IPlannedPurchasePriceState): void {
        this.store$.next(_state = state);
    }

    private setIsloading(isLoading: boolean): void {
        this.updateState({ ..._state, isLoading });
    }
}
